import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ApiPageAdapter,
  Page,
  PagedApiRequest,
  PagedApiResponse,
} from '@fmnts/api/shared';
import { ApiConfigService, ApiRequestHelper } from '@fmnts/api/util';
import { hasProperty, isString } from '@fmnts/core';
import { EMPTY, Observable, expand, map, reduce } from 'rxjs';
import { ApiNextQuizAdapter } from '../adapter/next-quiz.adapter';
import { IApiNextQuiz } from '../api-model/quiz.api';
import { NextQuiz } from '../model/quiz';

export declare type GetNextQuizListQuery = PagedApiRequest;
const hasNextPage = hasProperty('next', isString);

@Injectable({ providedIn: 'root' })
export class QuizNextApi {
  public static readonly rootUrl = '/api/next/v1/trainingcenter/quizzes';

  private readonly _fromApiNextQuizAdapter = new ApiNextQuizAdapter();
  private readonly _fromApiNextQuizPageAdapter = new ApiPageAdapter(
    this._fromApiNextQuizAdapter,
  );

  constructor(
    private http: HttpClient,
    private configService: ApiConfigService,
    private apiHelper: ApiRequestHelper,
  ) {}

  /**
   * Fetches a list of quizzes
   *
   * @returns a paginated list of quizzes, including the status for attempts
   */
  public list(query: GetNextQuizListQuery = {}): Observable<Page<NextQuiz>> {
    const url = this.configService.buildApiUrl([QuizNextApi.rootUrl]);
    const params = this.apiHelper.makeParams(query);

    return this.http
      .get<PagedApiResponse<IApiNextQuiz>>(url, { params })
      .pipe(
        map((response) => this._fromApiNextQuizPageAdapter.adapt(response)),
      );
  }

  /**
   * List of all quizzes assigned for the current fundraiser.
   * Calls the `list` endpoint, until all pages are fetched
   *
   * @returns
   * An `Observable` containing an array of all quizzes
   *
   */
  listAll(): Observable<NextQuiz[]> {
    return this.list().pipe(
      expand((response) =>
        hasNextPage(response)
          ? this.list({
              page: response.currentPage + 1,
            })
          : EMPTY,
      ),
      reduce((acc, current) => acc.concat(current.results), [] as NextQuiz[]),
    );
  }

  /**
   * Fetch a quiz using `quiz.id`
   *
   * @param id The id of the quiz
   * @returns A quiz, including the status for attempts
   */
  public get(id: string): Observable<NextQuiz> {
    const url = this.configService.buildApiUrl([QuizNextApi.rootUrl, id]);

    return this.http
      .get<IApiNextQuiz>(url)
      .pipe(map((response) => this._fromApiNextQuizAdapter.adapt(response)));
  }
}
